import { createBrowserRouter, RouterProvider } from 'react-router-dom';

import Loader from '@/components/Loader';
import { useAuth } from '@/providers/auth/useAuth';
import { ROUTES } from '@/router/router-url';
import { useAuthStore, UserRoles } from '@/store/useUser';
import { AuthStatusEnum } from '@/types/auth';
import { AREA_PAGE_GROUPS, ASSIGNMENT_PAGE_GROUPS, ATTENDANCE_GROUPS, hasAnyGroup } from '@/utils/vehicleAttendance';

import { ContinueRedirect } from '../components/ContinueRedirect';
import ContinueTo from '../components/ContinueTo';

const PUBLIC_ROUTER = createBrowserRouter([
  {
    lazy: () => import('@/layouts/AuthLayout'),
    children: [
      {
        index: true,
        path: ROUTES.LOGIN.PATH,
        lazy: () => import('@/pages/auth/Login')
      },
      {
        index: true,
        path: ROUTES.LOGIN_CLOUDFLARE.PATH,
        lazy: () => import('@/pages/auth/LoginCloudflare')
      },
      {
        index: true,
        path: ROUTES.INVITE_CREATE_USER.PATH,
        lazy: () => import('@/pages/invite-create-user/Index')
      },
      {
        index: true,
        path: ROUTES.FORGOT_PASSWORD.PATH,
        lazy: () => import('@/pages/auth/ForgotPassword')
      },
      {
        index: true,
        path: ROUTES.FORGOT.PATH,
        lazy: () => import('@/pages/auth/Forgot')
      },
      {
        // Kayıp dosyası belge talebi — hesapsız yükleme sayfası (mail linki)
        path: ROUTES.LOST_PACKAGES.RESPOND.PATH,
        lazy: () => import('@/pages/lost-packages/PublicRespond')
      }
    ]
  },
  {
    path: '*',
    element: <ContinueRedirect to={ROUTES.LOGIN.PATH} />
  }
]);

export default function Router() {
  const { login } = useAuth();
  const authStore = useAuthStore();

  if (login === AuthStatusEnum.LOADING) {
    return <Loader />;
  }

  if (login === AuthStatusEnum.LOGGED_OUT) {
    return <RouterProvider router={PUBLIC_ROUTER} fallbackElement={<Loader />} />;
  }

  if (login === AuthStatusEnum.LOGGED_IN) {
    if (authStore?.groups?.length > 0) {
      return (
        <RouterProvider
          router={createBrowserRouter([
            {
              lazy: () => import('@/layouts/Layout'),
              path: '/',
              children: [
                {
                  index: true,
                  path: ROUTES.INDEX.PATH,
                  lazy: () => import('@/pages/dashboard/Index')
                },
                {
                  index: true,
                  path: ROUTES.LOGIN.PATH,
                  element: <ContinueTo fail={ROUTES.INDEX.PATH} />
                },
                {
                  path: ROUTES.PROFILE.PATH,
                  lazy: () => import('@/pages/profile/Index')
                },
                {
                  path: ROUTES.MOBILIZ_VEHICLE_DETAIL.PATH,
                  lazy: () => import('@/pages/mobiliz-vehicle-detail/Index')
                },
                {
                  path: ROUTES.MOBILIZ_FLEET.PATH,
                  lazy: () => import('@/pages/mobiliz-fleet/Index')
                },
                {
                  path: ROUTES.ETABLOLAR.PATH,
                  lazy: () => import('@/pages/etablolar/Index')
                },
                // Taşıyıcı Olma Talepleri: ADMIN, SUPERADMIN ve FLEET_MANAGER (operasyon izni; CASL grubu
                // olmadığından rota her girişli kullanıcıya kayıtlı, yetki API'de güncel DB'den doğrulanır)
                {
                  path: ROUTES.FLEET_CARRIER_REQUESTS.PATH,
                  lazy: () => import('@/pages/fleet/carrier-requests/Index')
                },
                {
                  path: ROUTES.FLEET_CARRIER_REQUEST_DETAIL.PATH,
                  lazy: () => import('@/pages/fleet/carrier-requests/Detail')
                },
                // Filo modülü (plan: docs/FILO_MODULU_PLANI.md) — yalnız ADMIN (operasyon admin) ve SUPERADMIN
                ...(authStore?.groups?.includes(UserRoles.ADMIN) || authStore?.groups?.includes(UserRoles.SUPERADMIN)
                  ? [
                      {
                        path: ROUTES.FLEET_DASHBOARD.PATH,
                        lazy: () => import('@/pages/fleet/dashboard/Index')
                      },
                      {
                        path: ROUTES.FLEET_VEHICLES.PATH,
                        lazy: () => import('@/pages/fleet/vehicles/Index')
                      },
                      {
                        path: ROUTES.FLEET_VEHICLE_DETAIL.PATH,
                        lazy: () => import('@/pages/fleet/vehicles/Detail')
                      },
                      {
                        path: ROUTES.FLEET_VEHICLE_EDIT.PATH,
                        lazy: () => import('@/pages/fleet/vehicles/Edit')
                      },
                      {
                        path: ROUTES.FLEET_DRIVERS.PATH,
                        lazy: () => import('@/pages/fleet/drivers/Index')
                      },
                      {
                        path: ROUTES.FLEET_DRIVER_DETAIL.PATH,
                        lazy: () => import('@/pages/fleet/drivers/Detail')
                      },
                      {
                        path: ROUTES.FLEET_ASSIGNMENTS.PATH,
                        lazy: () => import('@/pages/fleet/assignments/Index')
                      },
                      {
                        path: ROUTES.FLEET_DOCUMENTS.PATH,
                        lazy: () => import('@/pages/fleet/documents/Index')
                      },
                      {
                        path: ROUTES.FLEET_AGENDA.PATH,
                        lazy: () => import('@/pages/fleet/agenda/Index')
                      },
                      {
                        path: ROUTES.FLEET_NOTIFICATIONS.PATH,
                        lazy: () => import('@/pages/fleet/notifications/Index')
                      },
                      {
                        path: ROUTES.FLEET_OPTION_SETS.PATH,
                        lazy: () => import('@/pages/fleet/definitions/OptionSets')
                      },
                      {
                        path: ROUTES.FLEET_CUSTOM_FIELDS.PATH,
                        lazy: () => import('@/pages/fleet/definitions/CustomFields')
                      },
                      {
                        path: ROUTES.FLEET_AUTOMATIONS.PATH,
                        lazy: () => import('@/pages/fleet/definitions/Automations')
                      },
                      {
                        path: ROUTES.FLEET_REMINDER_SETTINGS.PATH,
                        lazy: () => import('@/pages/fleet/definitions/ReminderSettings')
                      },
                      {
                        path: ROUTES.FLEET_AUDIT.PATH,
                        lazy: () => import('@/pages/fleet/definitions/Audit')
                      },
                      {
                        path: ROUTES.FLEET_WORK_ORDERS.PATH,
                        lazy: () => import('@/pages/fleet/maintenance/WorkOrders')
                      },
                      {
                        path: ROUTES.FLEET_WORK_ORDER_DETAIL.PATH,
                        lazy: () => import('@/pages/fleet/maintenance/WorkOrderDetail')
                      },
                      {
                        path: ROUTES.FLEET_PLANS.PATH,
                        lazy: () => import('@/pages/fleet/maintenance/Plans')
                      },
                      {
                        path: ROUTES.FLEET_TIRES.PATH,
                        lazy: () => import('@/pages/fleet/tires/Index')
                      },
                      {
                        path: ROUTES.FLEET_INSPECTIONS.PATH,
                        lazy: () => import('@/pages/fleet/inspections/Index')
                      },
                      {
                        path: ROUTES.FLEET_SUPPLIERS.PATH,
                        lazy: () => import('@/pages/fleet/suppliers/Index')
                      },
                      {
                        path: ROUTES.FLEET_CONTRACTS.PATH,
                        lazy: () => import('@/pages/fleet/suppliers/Contracts')
                      },
                      {
                        path: ROUTES.FLEET_POLICIES.PATH,
                        lazy: () => import('@/pages/fleet/insurance/Policies')
                      },
                      {
                        path: ROUTES.FLEET_OFFERS.PATH,
                        lazy: () => import('@/pages/fleet/insurance/Offers')
                      },
                      {
                        path: ROUTES.FLEET_DAMAGES.PATH,
                        lazy: () => import('@/pages/fleet/insurance/Damages')
                      },
                      {
                        path: ROUTES.FLEET_DAMAGE_DETAIL.PATH,
                        lazy: () => import('@/pages/fleet/insurance/DamageDetail')
                      },
                      {
                        path: ROUTES.FLEET_TOLLS.PATH,
                        lazy: () => import('@/pages/fleet/tolls/Tolls')
                      },
                      {
                        path: ROUTES.FLEET_FINES.PATH,
                        lazy: () => import('@/pages/fleet/tolls/Fines')
                      },
                      {
                        path: ROUTES.FLEET_EXPENSES.PATH,
                        lazy: () => import('@/pages/fleet/expenses/Expenses')
                      },
                      {
                        path: ROUTES.FLEET_BUDGETS.PATH,
                        lazy: () => import('@/pages/fleet/expenses/Budgets')
                      },
                      {
                        path: ROUTES.FLEET_TCO.PATH,
                        lazy: () => import('@/pages/fleet/expenses/Tco')
                      },
                      {
                        path: ROUTES.FLEET_REPORTS.PATH,
                        lazy: () => import('@/pages/fleet/expenses/Reports')
                      },
                      {
                        path: ROUTES.FLEET_MAP.PATH,
                        lazy: () => import('@/pages/fleet/map/Index')
                      },
                      {
                        path: ROUTES.FLEET_SCORING.PATH,
                        lazy: () => import('@/pages/fleet/scoring/Index')
                      },
                      {
                        path: ROUTES.FLEET_SCORING_DETAIL.PATH,
                        lazy: () => import('@/pages/fleet/scoring/Detail')
                      },
                      {
                        path: ROUTES.FLEET_SCHEMES.PATH,
                        lazy: () => import('@/pages/fleet/scoring/Schemes')
                      },
                      {
                        path: ROUTES.FLEET_INDICATORS.PATH,
                        lazy: () => import('@/pages/fleet/indicators/Index')
                      },
                      {
                        path: ROUTES.FLEET_TIMESHEET.PATH,
                        lazy: () => import('@/pages/fleet/timesheet/Index')
                      }
                    ]
                  : []),
                ...(authStore?.groups?.includes(UserRoles.SUPERADMIN) || authStore?.groups?.includes(UserRoles.HUMAN_RESOURCES)
                  ? [
                      {
                        path: ROUTES.CAREER_FORM.PATH,
                        lazy: () => import('@/pages/career-form/Index')
                      },
                      {
                        path: ROUTES.CAREER_FORM.DETAIL.PATH,
                        lazy: () => import('@/pages/career-form/Detail')
                      }
                    ]
                  : []),
                {
                  path: ROUTES.CUSTOMER_FORM.PATH,
                  lazy: () => import('@/pages/customer-form/Index')
                },
                {
                  path: ROUTES.CUSTOMER_FORM.DETAIL.PATH,
                  lazy: () => import('@/pages/customer-form/Detail')
                },
                {
                  path: ROUTES.CHAT_MESSAGES_LIST.PATH,
                  lazy: () => import('@/pages/chat-messages-list/Index'),
                  children: [
                    {
                      path: ROUTES.CHAT_MESSAGES_LIST.EDIT.PATH,
                      lazy: () => import('@/pages/chat-messages-list/Edit')
                    }
                  ]
                },
                {
                  path: ROUTES.CONVERSATIONS.PATH,
                  lazy: () => import('@/pages/conversations/Index'),
                  children: [
                    {
                      path: ROUTES.CONVERSATIONS.DETAIL.PATH,
                      lazy: () => import('@/pages/conversations/Detail')
                    }
                  ]
                },
                {
                  path: ROUTES.CURRENT_ROUTE_PLAN.PATH,
                  lazy: () => import('@/pages/current-route-plan/Index')
                },
                ...(authStore?.groups?.includes(UserRoles.OPERATION)
                  ? [
                      {
                        path: ROUTES.DISTRIBUTION_EXPEDITIONS.PATH,
                        lazy: () => import('@/pages/distribution-expeditions/Index')
                      },
                      {
                        path: ROUTES.DISTRIBUTION_EXPEDITIONS.CREATE.PATH,
                        lazy: () => import('@/pages/distribution-expeditions/Form')
                      },
                      {
                        path: ROUTES.DISTRIBUTION_EXPEDITIONS.EDIT.PATH,
                        lazy: () => import('@/pages/distribution-expeditions/Form')
                      },
                      {
                        path: ROUTES.DISTRIBUTION_EXPEDITIONS.DETAIL.PATH,
                        lazy: () => import('@/pages/distribution-expeditions/Detail')
                      }
                    ]
                  : []),
                {
                  path: ROUTES.SPECIAL_VEHICLE_SUGGESTIONS.PATH,
                  lazy: () => import('@/pages/special-vehicle-suggestions/Index')
                },
                {
                  path: ROUTES.CUSTOMER_VISITS.PATH,
                  lazy: () => import('@/pages/customer-visits/Index')
                },
                {
                  path: ROUTES.OPEN_POSITIONS.PATH,
                  lazy: () => import('@/pages/open-positions/Index')
                },
                {
                  path: ROUTES.OPEN_POSITIONS.DETAIL.PATH,
                  lazy: () => import('@/pages/open-positions/Detail')
                },
                {
                  path: ROUTES.OPEN_POSITIONS.EDIT.PATH,
                  lazy: () => import('@/pages/open-positions/Edit')
                },
                {
                  path: ROUTES.CHECKLIST.PATH,
                  lazy: () => import('@/pages/checklist/Index')
                },
                {
                  path: ROUTES.CHECKLIST.TASKS.PATH,
                  lazy: () => import('@/pages/checklist/Tasks')
                },
                {
                  path: ROUTES.CHECKLIST.TASKS_EDIT.PATH,
                  lazy: () => import('@/pages/checklist/TaskEdit')
                },
                {
                  path: ROUTES.CHECKLIST.REPORT.PATH,
                  lazy: () => import('@/pages/checklist/Report')
                },
                {
                  path: ROUTES.CUSTOMER_VISITS.DETAIL.PATH,
                  lazy: () => import('@/pages/customer-visits/Detail')
                },
                {
                  path: ROUTES.PACKAGE_TANZIM.PATH,
                  lazy: () => import('@/pages/package-tanzim/Index')
                },
                {
                  path: ROUTES.TICKETS.PATH,
                  lazy: () => import('@/pages/tickets/Index'),
                  children: [
                    {
                      path: ROUTES.TICKETS.EDIT.PATH,
                      lazy: () => import('@/pages/tickets/Edit')
                    }
                  ]
                },
                {
                  path: ROUTES.TICKETS.DETAIL.PATH,
                  lazy: () => import('@/pages/tickets/Detail')
                },
                ...(authStore?.groups?.includes(UserRoles.SUPERADMIN)
                  ? [
                      {
                        path: ROUTES.TICKET_TEAMS.PATH,
                        lazy: () => import('@/pages/tickets/Teams'),
                        children: [
                          {
                            path: ROUTES.TICKET_TEAMS.EDIT.PATH,
                            lazy: () => import('@/pages/tickets/TeamEdit')
                          }
                        ]
                      },
                      {
                        path: ROUTES.TICKET_TEAMS.DETAIL.PATH,
                        lazy: () => import('@/pages/tickets/TeamDetail')
                      }
                    ]
                  : []),
                {
                  path: ROUTES.TICKET_STATISTICS.PATH,
                  lazy: () => import('@/pages/tickets/Statistics')
                },
                {
                  path: ROUTES.FEEDBACK.PATH,
                  lazy: () => import('@/pages/feedback/Index'),
                  children: [
                    {
                      path: ROUTES.FEEDBACK.NEW.PATH,
                      lazy: () => import('@/pages/feedback/New')
                    }
                  ]
                },
                {
                  path: `${ROUTES.FEEDBACK.PATH}/${ROUTES.FEEDBACK.DETAIL.PATH}`,
                  lazy: () => import('@/pages/feedback/Detail')
                },
                ...(authStore?.groups?.includes(UserRoles.SUPERADMIN)
                  ? [
                      {
                        path: ROUTES.FEEDBACK_ADMIN.PATH,
                        lazy: () => import('@/pages/feedback/Admin')
                      }
                    ]
                  : []),
                {
                  path: ROUTES.CUSTOMER_VISITS.EDIT.PATH,
                  lazy: () => import('@/pages/customer-visits/Edit')
                },
                {
                  path: ROUTES.SUPPORT_REQUESTS.PATH,
                  lazy: () => import('@/pages/support-requests/Index')
                },
                {
                  path: ROUTES.SUPPORT_REQUESTS.DETAIL.PATH,
                  lazy: () => import('@/pages/support-requests/Detail')
                },
                {
                  path: ROUTES.COMPANY_MONITORING.PATH,
                  lazy: () => import('@/pages/company-monitoring/Index')
                },
                {
                  path: ROUTES.COMPANY_MONITORING.DETAIL.PATH,
                  lazy: () => import('@/pages/company-monitoring/Detail')
                },
                {
                  path: ROUTES.COMPANY_MONITORING.EDIT.PATH,
                  lazy: () => import('@/pages/company-monitoring/Edit')
                },
                {
                  path: ROUTES.TRANSFER_VEHICLE_ENTRANCE.PATH,
                  lazy: () => import('@/pages/transfer-vehicle-entrance/Index'),
                  children: [
                    {
                      path: ROUTES.TRANSFER_VEHICLE_ENTRANCE.EDIT.PATH,
                      lazy: () => import('@/pages/transfer-vehicle-entrance/Edit')
                    },
                    {
                      path: ROUTES.TRANSFER_VEHICLE_ENTRANCE.DETAIL.PATH,
                      lazy: () => import('@/pages/transfer-vehicle-entrance/Detail')
                    },
                    {
                      path: 'logs/:id',
                      lazy: () => import('@/pages/transfer-vehicle-entrance/Logs')
                    },
                    {
                      path: ROUTES.TRANSFER_VEHICLE_ENTRANCE.DELIVERY_NOTE.PATH,
                      lazy: () => import('@/pages/transfer-vehicle-entrance/DeliveryNote')
                    }
                  ]
                },
                {
                  path: ROUTES.PANEL_NOTIFICATIONS.PATH,
                  lazy: () => import('@/pages/panel-notifications/Index'),
                  children: [
                    {
                      path: ROUTES.PANEL_NOTIFICATIONS.EDIT.PATH,
                      lazy: () => import('@/pages/panel-notifications/Edit')
                    }
                  ]
                },
                {
                  path: ROUTES.MY_NOTIFICATIONS.PATH,
                  lazy: () => import('@/pages/my-notifications/Index')
                },
                // Organizasyon & Bildirim yönetimi (plan docs/BILDIRIM_TAKIMLARI_PLANI.md §7.1). Rotalar herkese kayıtlı;
                // sayfaları SuperadminOnly sarar: rol yoksa 403 sonucu gösterilir ve istek atılmaz (sözleşme §0.1). Menü
                // yalnız SUPERADMIN'e görünür; asıl koruma BE SuperadminOnlyGuard.
                {
                  path: ROUTES.ORG_TITLES.PATH,
                  lazy: () => import('@/pages/org-titles/Index')
                },
                {
                  path: ROUTES.ORG_STRUCTURE.PATH,
                  lazy: () => import('@/pages/org-structure/Index')
                },
                {
                  path: ROUTES.NOTIFICATION_TOPICS.PATH,
                  lazy: () => import('@/pages/notification-topics/Index')
                },
                {
                  path: ROUTES.NOTIFICATION_HISTORY.PATH,
                  lazy: () => import('@/pages/notification-history/Index')
                },
                {
                  path: ROUTES.ORG_CHANGE_LOGS.PATH,
                  lazy: () => import('@/pages/org-change-logs/Index')
                },
                // Barkod Uyarıları: hem SUPERADMIN hem OPERATION (operasyon.admin)
                // oluşturabilsin. Paket detayındaki "Barkod Uyarısı Ekle" butonu iki
                // role de göründüğü için route da iki role açık olmalı; aksi halde
                // OPERATION'da route bulunamayıp 404'e düşüyordu.
                // Cihaz Teşhis: kuryeApp telemetri kayıtları. Menüde "Destek"
                // altında duruyor; o grup SUPERADMIN/ADMIN/SUPPORT'a görünüyor,
                // dolayısıyla route da EN AZ o rolleri kapsamalı — aksi halde
                // menüde görünüp tıklayınca 404'e düşülür (barkod uyarılarında
                // yaşanan hata). OPERATION da saha sorunlarını izleyebilsin.
                ...(authStore?.groups?.includes(UserRoles.SUPERADMIN) ||
                authStore?.groups?.includes(UserRoles.ADMIN) ||
                authStore?.groups?.includes(UserRoles.SUPPORT) ||
                authStore?.groups?.includes(UserRoles.OPERATION)
                  ? [
                      {
                        path: ROUTES.DEVICE_DIAGNOSTICS.PATH,
                        lazy: () => import('@/pages/device-diagnostics/Index')
                      }
                    ]
                  : []),
                // Oturum kayıtları (session replay) — SUPERADMIN + SUPPORT (karar K5); ayarlar yalnız SUPERADMIN
                ...(authStore?.groups?.includes(UserRoles.SUPERADMIN) || authStore?.groups?.includes(UserRoles.SUPPORT)
                  ? [
                      {
                        path: ROUTES.REPLAY.PATH,
                        lazy: () => import('@/pages/replay/Index')
                      },
                      {
                        path: ROUTES.REPLAY.DETAIL.PATH,
                        lazy: () => import('@/pages/replay/Detail')
                      }
                    ]
                  : []),
                ...(authStore?.groups?.includes(UserRoles.SUPERADMIN)
                  ? [
                      {
                        path: ROUTES.REPLAY.SETTINGS.PATH,
                        lazy: () => import('@/pages/replay/Settings')
                      }
                    ]
                  : []),
                // Okutmada Sıfır Desi raporu (plan v1.3 §5-2): OPERATION/ADMIN/SUPERADMIN —
                // sunucu ScanZeroReportGuard ile aynı küme; menü de bu rollere görünür.
                ...(authStore?.groups?.includes(UserRoles.SUPERADMIN) ||
                authStore?.groups?.includes(UserRoles.ADMIN) ||
                authStore?.groups?.includes(UserRoles.OPERATION)
                  ? [
                      {
                        path: ROUTES.SCAN_ZERO_DESI.PATH,
                        lazy: () => import('@/pages/scan-zero-desi/Index')
                      }
                    ]
                  : []),
                ...(authStore?.groups?.includes(UserRoles.SUPERADMIN) || authStore?.groups?.includes(UserRoles.OPERATION)
                  ? [
                      {
                        path: ROUTES.BARCODE_ALERTS.PATH,
                        lazy: () => import('@/pages/barcode-alerts/Index'),
                        children: [
                          {
                            path: ROUTES.BARCODE_ALERTS.EDIT.PATH,
                            lazy: () => import('@/pages/barcode-alerts/Edit')
                          }
                        ]
                      }
                    ]
                  : []),
                ...(authStore?.groups?.includes(UserRoles.SUPERADMIN)
                  ? [
                      {
                        path: ROUTES.DESKTOP_REPORT_USERS.PATH,
                        lazy: () => import('@/pages/desktop-report-users/Index')
                      },
                      {
                        path: ROUTES.SESSION_INVALIDATION_LOGS.PATH,
                        lazy: () => import('@/pages/session-invalidation-logs/Index')
                      },
                      {
                        path: ROUTES.MULTI_SESSION_USERS.PATH,
                        lazy: () => import('@/pages/multi-session-users/Index'),
                        children: [
                          {
                            path: ROUTES.MULTI_SESSION_USERS.EDIT.PATH,
                            lazy: () => import('@/pages/multi-session-users/Edit')
                          }
                        ]
                      },
                      {
                        path: ROUTES.SAVED_REPORTS.PATH,
                        lazy: () => import('@/pages/saved-reports/Index')
                      },
                      {
                        path: ROUTES.DEALER_GROUPS.PATH,
                        lazy: () => import('@/pages/dealer-groups/Index'),
                        children: [
                          {
                            path: ROUTES.DEALER_GROUPS.EDIT.PATH,
                            lazy: () => import('@/pages/dealer-groups/Edit')
                          }
                        ]
                      },
                      {
                        path: ROUTES.DEALER_GROUPS.DETAIL.PATH,
                        lazy: () => import('@/pages/dealer-groups/Detail')
                      },
                      {
                        path: `${ROUTES.DEALER_GROUPS.PATH}/analyze`,
                        lazy: () => import('@/pages/dealer-groups/Analyze')
                      },
                      {
                        path: ROUTES.ACTIVITIES.PATH,
                        lazy: () => import('@/pages/activities/Index')
                      },
                      {
                        path: ROUTES.AGREEMENT_LOGS.PATH,
                        lazy: () => import('@/pages/agreement-logs/Index')
                      },
                      {
                        path: ROUTES.COMPANY_LOGS.PATH,
                        lazy: () => import('@/pages/company-logs/Index')
                      },
                      {
                        path: ROUTES.VENDOR_LOGS.PATH,
                        lazy: () => import('@/pages/vendor-logs/Index')
                      },
                      {
                        path: ROUTES.POINT_LOGS.PATH,
                        lazy: () => import('@/pages/point-logs/Index')
                      },
                      {
                        path: ROUTES.POINT_MAP_LOGS.PATH,
                        lazy: () => import('@/pages/point-map-logs/Index')
                      },
                      {
                        path: ROUTES.POINT_MAP_ADMIN.PATH,
                        lazy: () => import('@/pages/point-map-admin/Index')
                      },
                      {
                        path: ROUTES.VEHICLE_LOGS.PATH,
                        lazy: () => import('@/pages/vehicle-logs/Index')
                      },
                      {
                        path: ROUTES.ROUTE_LOGS.PATH,
                        lazy: () => import('@/pages/route-logs/Index')
                      },
                      {
                        path: ROUTES.ROUTE_SESSION_LOGS.PATH,
                        lazy: () => import('@/pages/route-session-logs/Index')
                      },
                      {
                        path: ROUTES.TRANSFER_VEHICLE_ENTRY_LOGS.PATH,
                        lazy: () => import('@/pages/transfer-vehicle-entry-logs/Index')
                      },
                      {
                        path: ROUTES.VEHICLE_PLAN_LOGS.PATH,
                        lazy: () => import('@/pages/vehicle-plan-logs/Index')
                      },
                      {
                        path: ROUTES.USER_LOGS.PATH,
                        lazy: () => import('@/pages/user-logs/Index')
                      },
                      {
                        path: ROUTES.ZERO_DESI_LOGS.PATH,
                        lazy: () => import('@/pages/zero-desi-logs/Index')
                      },
                      {
                        path: ROUTES.DOCUMENT_LOGS.PATH,
                        lazy: () => import('@/pages/document-logs/Index')
                      },
                      {
                        path: ROUTES.AUDIT_LOGS.PATH,
                        lazy: () => import('@/pages/audit-logs/Index')
                      },
                      {
                        path: ROUTES.MCP_AUDIT.PATH,
                        lazy: () => import('@/pages/mcp-audit/Index')
                      },
                      {
                        path: ROUTES.OKUTMA_TESHIS.PATH,
                        lazy: () => import('@/pages/okutma-teshis/Index')
                      },
                      {
                        path: ROUTES.SANTRAL_API.PATH,
                        lazy: () => import('@/pages/santral-api/Index')
                      },
                      {
                        path: ROUTES.INSURER_ACCESS.PATH,
                        lazy: () => import('@/pages/insurer-access/Index'),
                        children: [
                          {
                            path: ROUTES.INSURER_ACCESS.GRANT.PATH,
                            lazy: () => import('@/pages/insurer-access/GrantEdit')
                          },
                          {
                            path: ROUTES.INSURER_ACCESS.GRANT_DETAIL.PATH,
                            lazy: () => import('@/pages/insurer-access/GrantDetail')
                          }
                        ]
                      },
                      {
                        path: ROUTES.INSURERS.PATH,
                        lazy: () => import('@/pages/insurer-access/InsurersIndex'),
                        children: [
                          {
                            path: ROUTES.INSURERS.INSURER.PATH,
                            lazy: () => import('@/pages/insurer-access/InsurerEdit')
                          }
                        ]
                      },
                      {
                        path: ROUTES.DELIVERY_TIME_LOGS.PATH,
                        lazy: () => import('@/pages/delivery-time-logs/Index')
                      },
                      {
                        path: ROUTES.PRICE_ESTIMATE_LOGS.PATH,
                        lazy: () => import('@/pages/price-estimate-logs/Index')
                      },
                      {
                        path: ROUTES.BULK_RECALC_ROUTE.PATH,
                        lazy: () => import('@/pages/bulk-recalc-route/Index')
                      },
                      {
                        path: ROUTES.BULK_CALC_PRICE.PATH,
                        lazy: () => import('@/pages/bulk-calc-price/Index')
                      }
                    ]
                  : []),
                {
                  path: ROUTES.ACCEPT_INVITE.PATH,
                  lazy: () => import('@/pages/accept-invite/Index')
                },
                {
                  path: ROUTES.CSV_VENDOR_CREATE.PATH,
                  lazy: () => import('@/pages/csv-vendor-create/Index')
                },
                {
                  path: ROUTES.CSV_INVENTORY_CREATE.PATH,
                  lazy: () => import('@/pages/csv-inventory-create/Index')
                },
                {
                  path: ROUTES.BULK_PRINTING.RETURN_SHIPMENTS.PATH,
                  lazy: () => import('@/pages/bulk-printing/ReturnShipments')
                },
                {
                  path: ROUTES.BULK_PRINTING.SHIPMENTS.PATH,
                  lazy: () => import('@/pages/bulk-printing/Shipments')
                },
                {
                  path: ROUTES.BULK_PRINTING.INTER_DEALER_SHIPMENT.PATH,
                  lazy: () => import('@/pages/bulk-printing/InterDealerShipment')
                },
                {
                  path: ROUTES.APPLOGIN.PATH,
                  lazy: () => import('@/pages/auth/AppLogin')
                },
                {
                  path: ROUTES.SHIPMENTS.PATH,
                  lazy: () => import('@/pages/shipments/Index')
                },
                {
                  path: ROUTES.INTER_DEALER_SHIPMENT.PATH,
                  lazy: () => import('@/pages/inter-dealer-shipment/Index')
                },
                {
                  path: ROUTES.RETURN_SHIPMENTS.PATH,
                  lazy: () => import('@/pages/return-shipments/Index')
                },
                {
                  path: ROUTES.DAMAGED_SHIPMENTS.PATH,
                  lazy: () => import('@/pages/damaged-shipments/Index')
                },
                {
                  path: ROUTES.DESI_CHANGED_PACKAGES.PATH,
                  lazy: () => import('@/pages/desi-changed-packages/Index')
                },
                {
                  path: ROUTES.ZERO_DESI_UPDATE.PATH,
                  lazy: () => import('@/pages/zero-desi-update/Index')
                },
                {
                  path: ROUTES.OLUSTURULDU_ISLEMLERI.PATH,
                  lazy: () => import('@/pages/olusturuldu-islemleri/Index')
                },
                {
                  path: ROUTES.ONE_DESI_REPORT.PATH,
                  lazy: () => import('@/pages/one-desi-report/Index')
                },
                {
                  path: ROUTES.COMPANY_DESI_RANGES.PATH,
                  lazy: () => import('@/pages/company-desi-ranges/Index')
                },
                {
                  path: ROUTES.SUPER_ADMIN_HISTORY.PATH,
                  lazy: () => import('@/pages/super-admin-history/Index')
                },
                {
                  path: ROUTES.SUPER_ADMIN_HISTORY.DETAIL.PATH,
                  lazy: () => import('@/pages/super-admin-history/Detail')
                },
                {
                  path: ROUTES.REPORTS.PERSONEL_LOG.PATH,
                  lazy: () => import('@/pages/reports/PersonelLog')
                },
                {
                  path: ROUTES.REPORTS.LOST_PACKAGES.PATH,
                  lazy: () => import('@/pages/reports/LostPackages')
                },
                {
                  path: ROUTES.REPORTS.SHIPMENTS.PATH,
                  lazy: () => import('@/pages/reports/Shipments')
                },
                {
                  path: ROUTES.REPORTS.ALL_SHIPMENTS.PATH,
                  lazy: () => import('@/pages/reports/AllShipments')
                },
                {
                  path: ROUTES.REPORTS.INTER_DEALER_SHIPMENT.PATH,
                  lazy: () => import('@/pages/reports/InterDealerShipments')
                },
                {
                  path: ROUTES.REPORTS.RETURN_SHIPMENTS.PATH,
                  lazy: () => import('@/pages/reports/ReturnShipments')
                },
                {
                  path: ROUTES.REPORTS.MONTHLY_COMPANY_REPORT.PATH,
                  lazy: () => import('@/pages/reports/MonthlyCompanyReport')
                },
                {
                  path: ROUTES.REPORTS.WEEKLY_COMPANY_REPORT.PATH,
                  lazy: () => import('@/pages/reports/WeeklyCompanyReport')
                },
                {
                  path: ROUTES.REPORTS.DAILY_COMPANY_REPORT.PATH,
                  lazy: () => import('@/pages/reports/DailyCompanyReport')
                },
                {
                  path: ROUTES.OLD_RETURN_PACKAGE.PATH,
                  lazy: () => import('@/pages/old-return-package/Index')
                },
                {
                  path: ROUTES.REPORTS.DAMAGE_SHIPMENTS.PATH,
                  lazy: () => import('@/pages/reports/DamagedShipments')
                },
                {
                  path: ROUTES.REGION_TRANSFER_TYPES.PATH,
                  lazy: () => import('@/pages/region-transfer-types/Index'),
                  children: [
                    {
                      path: ROUTES.REGION_TRANSFER_TYPES.EDIT.PATH,
                      lazy: () => import('@/pages/region-transfer-types/Edit')
                    }
                  ]
                },
                {
                  path: ROUTES.REPORTS.BY_ID.PATH,
                  lazy: () => import('@/pages/reports/ById')
                },
                {
                  path: ROUTES.SHIPMENTS.DETAIL.PATH,
                  lazy: () => import('@/pages/shipments/Detail')
                },
                ...(useAuthStore.getState()?.groups?.includes(UserRoles.COMPANY_ADMIN) ||
                useAuthStore.getState()?.groups?.includes(UserRoles.VENDOR_ADMIN) ||
                useAuthStore.getState()?.groups?.includes(UserRoles.VENDOR) ||
                useAuthStore.getState()?.groups?.includes(UserRoles.COMPANY) ||
                useAuthStore.getState()?.groups?.includes(UserRoles.ADMIN)
                  ? [
                      {
                        path: ROUTES.INVITED_USERS.PATH,
                        lazy: () => import('@/pages/invited-users/Index')
                      }
                    ]
                  : []),
                {
                  path: ROUTES.USER.PATH,
                  lazy: () => import('@/pages/user/Index'),
                  children: [
                    {
                      path: ROUTES.USER.USER_INVITE.PATH,
                      lazy: () => import('@/pages/user/UserInvite')
                    },
                    ...(authStore?.groups?.includes(UserRoles.ADMIN)
                      ? [
                          {
                            path: ROUTES.USER.EDIT.PATH,
                            lazy: () => import('@/pages/user/Edit')
                          },
                          {
                            path: ROUTES.USER.USER_PERMISSIONS.PATH,
                            lazy: () => import('@/pages/user/UserPermissions')
                          }
                        ]
                      : [])
                  ]
                },
                {
                  path: ROUTES.VENDORS.PATH,
                  lazy: () => import('@/pages/vendor/Index'),
                  children: [
                    ...(authStore?.groups?.includes(UserRoles.ADMIN) ||
                    authStore?.groups?.includes(UserRoles.XDOCK_ADMIN) ||
                    authStore?.groups?.includes(UserRoles.XDOCK) ||
                    authStore?.groups?.includes(UserRoles.COMPANY_ADMIN) ||
                    authStore?.groups?.includes(UserRoles.VENDOR_ADMIN)
                      ? [
                          {
                            path: ROUTES.VENDORS.EDIT.PATH,
                            lazy: () => import('@/pages/vendor/Edit')
                          },
                          {
                            path: ROUTES.VENDORS.DATE_CHANGE.PATH,
                            lazy: () => import('@/pages/vendor/DateChange')
                          }
                        ]
                      : []),
                    ...(authStore?.groups?.includes(UserRoles.ADMIN) ||
                    authStore?.groups?.includes(UserRoles.XDOCK_ADMIN) ||
                    authStore?.groups?.includes(UserRoles.COMPANY_DEPO)
                      ? [
                          {
                            path: ROUTES.VENDORS.ROUTE_CHANGE.PATH,
                            lazy: () => import('@/pages/vendor/RouteChange')
                          }
                        ]
                      : [])
                  ]
                },
                {
                  path: ROUTES.DOCUMENT.PATH,
                  lazy: () => import('@/pages/document/Index'),
                  children: [
                    ...(authStore?.groups?.includes(UserRoles.ADMIN)
                      ? [
                          {
                            path: ROUTES.DOCUMENT.EDIT.PATH,
                            lazy: () => import('@/pages/document/Edit')
                          }
                        ]
                      : [])
                  ]
                },
                {
                  path: ROUTES.VENDORS.DETAIL.PATH,
                  lazy: () => import('@/pages/vendor/Detail')
                },
                ...(authStore?.groups?.some((role) =>
                  [
                    UserRoles.ADMIN,
                    UserRoles.XDOCK_ADMIN,
                    UserRoles.XDOCK,
                    UserRoles.COMPANY_ADMIN,
                    UserRoles.COMPANY,
                    UserRoles.SUPPLIER_USER
                  ].includes(role)
                )
                  ? [
                      {
                        path: ROUTES.COMPANIES.PATH,
                        lazy: () => import('@/pages/companies/Index'),
                        children: [
                          {
                            path: ROUTES.COMPANIES.USERS.PATH,
                            lazy: () => import('@/pages/companies/Users')
                          },
                          ...(authStore?.groups?.some((role) =>
                            [UserRoles.ADMIN, UserRoles.XDOCK_ADMIN, UserRoles.XDOCK, UserRoles.COMPANY_ADMIN].includes(role)
                          )
                            ? [
                                {
                                  path: ROUTES.COMPANIES.EDIT.PATH,
                                  lazy: () => import('@/pages/companies/Edit')
                                },
                                ...(authStore?.groups?.includes(UserRoles.ADMIN)
                                  ? [
                                      // {
                                      //   path: ROUTES.COMPANIES.ROUTE_CHANGE.PATH,
                                      //   lazy: () => import('@/pages/companies/RouteChange')
                                      // },
                                      {
                                        path: ROUTES.COMPANIES.RETURN_ROUTE_EDIT.PATH,
                                        lazy: () => import('@/pages/companies/ReturnRouteEdit')
                                      },
                                      {
                                        path: ROUTES.COMPANIES.COLLECTION_ROUTE_EDIT.PATH,
                                        lazy: () => import('@/pages/companies/CollectionRouteEdit')
                                      }
                                    ]
                                  : [])
                              ]
                            : [])
                        ]
                      }
                    ]
                  : []),
                {
                  path: ROUTES.COMPANIES.DETAIL.PATH,
                  lazy: () => import('@/pages/companies/Detail')
                },

                ...(authStore?.groups?.includes(UserRoles.ADMIN)
                  ? [
                      {
                        path: ROUTES.COMPANY_CATEGORIES.PATH,
                        lazy: () => import('@/pages/company-categories/Index')
                      },
                      {
                        path: `${ROUTES.COMPANY_CATEGORIES.PATH}/edit/:id`,
                        lazy: () => import('@/pages/company-categories/Edit')
                      },
                      {
                        path: `${ROUTES.COMPANY_CATEGORIES.PATH}/kategorisiz`,
                        lazy: () => import('@/pages/company-categories/Uncategorized')
                      },
                      {
                        path: `${ROUTES.COMPANY_CATEGORIES.PATH}/:id`,
                        lazy: () => import('@/pages/company-categories/Detail')
                      }
                    ]
                  : []),
                {
                  path: ROUTES.ZAIMBOT.PATH,
                  lazy: () => import('@/pages/zaimbot/Index')
                },
                {
                  path: ROUTES.AGENTZAIM.PATH,
                  lazy: () => import('@/pages/agentzaim/Index')
                },
                ...(authStore?.groups?.includes(UserRoles.ADMIN) || authStore?.groups?.includes(UserRoles.COMPANY_ADMIN)
                  ? [
                      {
                        path: ROUTES.APPS.PATH,
                        lazy: () => import('@/pages/apps/Index'),
                        children: [
                          {
                            path: ROUTES.APPS.EDIT.PATH,
                            lazy: () => import('@/pages/apps/Edit')
                          },
                          {
                            path: ROUTES.APPS.DETAIL.PATH,
                            lazy: () => import('@/pages/apps/Detail')
                          }
                        ]
                      },
                      {
                        path: ROUTES.WEBHOOKS.PATH,
                        lazy: () => import('@/pages/webhooks/Index'),
                        children: [
                          {
                            path: ROUTES.APPS.EDIT.PATH,
                            lazy: () => import('@/pages/webhooks/Edit')
                          }
                        ]
                      }
                    ]
                  : []),
                ...(authStore?.groups?.includes(UserRoles.ADMIN) ||
                authStore?.groups?.includes(UserRoles.XDOCK_ADMIN) ||
                authStore?.groups?.includes(UserRoles.XDOCK) ||
                authStore?.groups?.includes(UserRoles.COMPANY_DEPO) ||
                authStore?.groups?.includes(UserRoles.SUPPLIER_USER)
                  ? [
                      {
                        path: ROUTES.DAILY_ROUTE.PATH,
                        lazy: () => import('@/pages/daily-route/Index'),
                        children: [
                          {
                            path: ROUTES.DAILY_ROUTE.EDIT.PATH,
                            lazy: () => import('@/pages/daily-route/Edit')
                          },
                          {
                            path: ROUTES.DAILY_ROUTE.DAILY_ROUTE_DRIVER_EDIT.PATH,
                            lazy: () => import('@/pages/daily-route/DriverEditModal')
                          },
                          {
                            path: ROUTES.DAILY_ROUTE.FILE_UPLOAD.PATH,
                            lazy: () => import('@/pages/daily-route/FileUplaod')
                          },
                          {
                            path: ROUTES.DAILY_ROUTE.REPORT_DOWNLOAD.PATH,
                            lazy: () => import('@/pages/daily-route/ReportDownload')
                          },
                          {
                            path: ROUTES.DAILY_ROUTE.VEHICLE_CHANGE.PATH,
                            lazy: () => import('@/pages/daily-route/VehicleChange')
                          },
                          {
                            path: ROUTES.DAILY_ROUTE.FILES.PATH,
                            lazy: () => import('@/pages/daily-route/Files')
                          },
                          {
                            path: ROUTES.DAILY_ROUTE.INTERMEDIATE_TRANSFER_DELIVERY_NOTE.PATH,
                            lazy: () => import('@/pages/daily-route/IntermediateTransferDeliveryNote')
                          },
                          {
                            path: ROUTES.DAILY_ROUTE.DELIVERY_NOTE.PATH,
                            lazy: () => import('@/pages/daily-route/DeliveryNote')
                          }
                        ]
                      },
                      {
                        path: `${ROUTES.DAILY_ROUTE.PATH}/${ROUTES.DAILY_ROUTE.INVOICE_UPLOAD.PATH}`,
                        lazy: () => import('@/pages/daily-route/InvoiceUpload')
                      }
                    ]
                  : []),
                ...(authStore?.groups?.includes(UserRoles.ADMIN) ||
                authStore?.groups?.includes(UserRoles.XDOCK_ADMIN) ||
                authStore?.groups?.includes(UserRoles.XDOCK) ||
                authStore?.groups?.includes(UserRoles.SUPERADMIN)
                  ? [
                      {
                        path: ROUTES.INVOICES.PATH,
                        lazy: () => import('@/pages/invoices/Index')
                      },
                      {
                        path: ROUTES.ROUTE_SESSION_STATISTICS.PATH,
                        lazy: () => import('@/pages/route-session-statistics/Index')
                      }
                    ]
                  : []),
                ...(authStore?.groups?.includes(UserRoles.ADMIN) || authStore?.groups?.includes(UserRoles.SUPERADMIN)
                  ? [
                      {
                        path: ROUTES.FULL_TRUCK_LOAD.PATH,
                        lazy: () => import('@/pages/full-truck-load/Index'),
                        children: [
                          {
                            path: ROUTES.FULL_TRUCK_LOAD.EDIT.PATH,
                            lazy: () => import('@/pages/full-truck-load/Edit')
                          }
                        ]
                      }
                    ]
                  : []),
                {
                  path: ROUTES.DAILY_ROUTE.DETAIL.PATH,
                  lazy: () => import('@/pages/daily-route/Detail')
                },
                {
                  path: ROUTES.WEIGHBRIDGE.PATH,
                  lazy: () => import('@/pages/weighbridge/Index')
                },
                ...(authStore?.groups?.includes(UserRoles.ADMIN) ||
                authStore?.groups?.includes(UserRoles.XDOCK_ADMIN) ||
                authStore?.groups?.includes(UserRoles.COMPANY_ADMIN)
                  ? [
                      {
                        path: ROUTES.BARCODE.EDIT.PATH,
                        lazy: () => import('@/pages/barcode/Edit')
                      }
                    ]
                  : []),
                ...(authStore?.groups?.includes(UserRoles.ADMIN) ||
                authStore?.groups?.includes(UserRoles.XDOCK_ADMIN) ||
                authStore?.groups?.includes(UserRoles.XDOCK) ||
                authStore?.groups?.includes(UserRoles.COMPANY_ADMIN) ||
                authStore?.groups?.includes(UserRoles.VENDOR_ADMIN)
                  ? [
                      {
                        path: ROUTES.BARCODE.RETURN_BARCODE_CREATE.PATH,
                        lazy: () => import('@/pages/barcode/ReturnBarcodeCreate')
                      },
                      {
                        path: ROUTES.BARCODE.LOST_BARCODE_CREATE.PATH,
                        lazy: () => import('@/pages/barcode/LostBarcodCreate')
                      },
                      {
                        path: ROUTES.MANUEL_BARCODE_PRINT.PATH,
                        lazy: () => import('@/pages/barcode/ManuelBarcodePrint')
                      },
                      {
                        path: ROUTES.BARCODE.V2V_BARCODE_CREATE.PATH,
                        lazy: () => import('@/pages/barcode/V2VBarcodeCreate')
                      }
                    ]
                  : []),
                {
                  path: ROUTES.FORD_INTEGRATION_TRIGGER.PATH,
                  lazy: () => import('@/pages/ford-integration-trigger/Index')
                },
                {
                  path: ROUTES.LOST_PACKAGES.PATH,
                  lazy: () => import('@/pages/lost-packages/Index'),
                  children: [
                    ...(authStore?.groups?.includes(UserRoles.ADMIN) ||
                    authStore?.groups?.includes(UserRoles.XDOCK_ADMIN) ||
                    authStore?.groups?.includes(UserRoles.XDOCK) ||
                    authStore?.groups?.includes(UserRoles.COMPANY_ADMIN) ||
                    authStore?.groups?.includes(UserRoles.VENDOR_ADMIN)
                      ? [
                          {
                            path: ROUTES.LOST_PACKAGES.EDIT.PATH,
                            lazy: () => import('@/pages/lost-packages/Edit')
                          }
                        ]
                      : [])
                  ]
                },
                {
                  path: ROUTES.LOST_PACKAGES.DETAIL.PATH,
                  lazy: () => import('@/pages/lost-packages/Detail')
                },
                {
                  path: ROUTES.PACKAGE_REQUEST.PATH,
                  lazy: () => import('@/pages/package-request/Index'),
                  children: [
                    {
                      path: ROUTES.PACKAGE_REQUEST.EDIT.PATH,
                      lazy: () => import('@/pages/package-request/Edit')
                    }
                  ]
                },
                {
                  path: ROUTES.PACKAGE_REQUEST.DETAIL.PATH,
                  lazy: () => import('@/pages/package-request/Detail')
                },
                {
                  path: ROUTES.DESI_ADET_KONTROL.PATH,
                  lazy: () => import('@/pages/desi-adet-kontrol/Index')
                },
                {
                  path: ROUTES.DESI_ADET_KONTROL.CREATE.PATH,
                  lazy: () => import('@/pages/desi-adet-kontrol/Create')
                },
                {
                  path: ROUTES.DESI_ADET_KONTROL.DETAIL.PATH,
                  lazy: () => import('@/pages/desi-adet-kontrol/Detail')
                },
                {
                  path: ROUTES.DESI_ADET_KONTROL_LOGS.PATH,
                  lazy: () => import('@/pages/desi-adet-kontrol/Logs')
                },
                {
                  path: ROUTES.EXCEL_PACKAGE_PRICE_UPDATE.PATH,
                  lazy: () => import('@/pages/excel-paket-fiyat-guncelle/Index')
                },
                {
                  path: ROUTES.LOST_PACKAGES.HELP.PATH,
                  lazy: () => import('@/pages/lost-packages/Help')
                },
                {
                  path: ROUTES.TRANSFER_AREA.PATH,
                  lazy: () => import('@/pages/transfer-area/Index')
                },
                {
                  path: ROUTES.VEHICLE_CATEGORY.PATH,
                  lazy: () => import('@/pages/vehicle-category/Index'),
                  children: [
                    {
                      path: ROUTES.VEHICLE_CATEGORY.EDIT.PATH,
                      lazy: () => import('@/pages/vehicle-category/Edit')
                    }
                  ]
                },
                ...(authStore?.groups?.includes(UserRoles.ADMIN) ||
                authStore?.groups?.includes(UserRoles.COMPANY_ADMIN) ||
                authStore?.groups?.includes(UserRoles.COMPANY_DEPO)
                  ? [
                      {
                        path: ROUTES.VEHICLE_REQUEST.PATH,
                        lazy: () => import('@/pages/vehicle-request/Index'),
                        children: [
                          {
                            path: ROUTES.VEHICLE_REQUEST.EDIT.PATH,
                            lazy: () => import('@/pages/vehicle-request/Edit')
                          }
                        ]
                      },
                      {
                        path: ROUTES.VEHICLE_REQUEST.DETAIL.PATH,
                        lazy: () => import('@/pages/vehicle-request/Detail')
                      },
                      {
                        path: ROUTES.COUNTRIES.PATH,
                        lazy: () => import('@/pages/countries/Index'),
                        children: [
                          ...(authStore?.groups?.includes(UserRoles.ADMIN)
                            ? [
                                {
                                  path: ROUTES.COUNTRIES.EDIT.PATH,
                                  lazy: () => import('@/pages/countries/Edit')
                                }
                              ]
                            : [])
                        ]
                      },
                      {
                        path: ROUTES.CITIES.PATH,
                        lazy: () => import('@/pages/cities/Index'),
                        children: [
                          ...(authStore?.groups?.includes(UserRoles.ADMIN)
                            ? [
                                {
                                  path: ROUTES.CITIES.EDIT.PATH,
                                  lazy: () => import('@/pages/cities/Edit')
                                }
                              ]
                            : [])
                        ]
                      },
                      {
                        path: ROUTES.DISTRICTS.PATH,
                        lazy: () => import('@/pages/districts/Index'),
                        children: [
                          ...(authStore?.groups?.includes(UserRoles.ADMIN)
                            ? [
                                {
                                  path: ROUTES.DISTRICTS.EDIT.PATH,
                                  lazy: () => import('@/pages/districts/Edit')
                                }
                              ]
                            : [])
                        ]
                      },
                      {
                        path: ROUTES.NEIGHBOURHOODS.PATH,
                        lazy: () => import('@/pages/neighbourhoods/Index'),
                        children: [
                          ...(authStore?.groups?.includes(UserRoles.ADMIN)
                            ? [
                                {
                                  path: ROUTES.NEIGHBOURHOODS.EDIT.PATH,
                                  lazy: () => import('@/pages/neighbourhoods/Edit')
                                }
                              ]
                            : [])
                        ]
                      },
                      {
                        path: ROUTES.CONTAINERS.PATH,
                        lazy: () => import('@/pages/containers/Index'),
                        children: [
                          ...(authStore?.groups?.includes(UserRoles.ADMIN)
                            ? [
                                {
                                  path: ROUTES.CONTAINERS.EDIT.PATH,
                                  lazy: () => import('@/pages/containers/Edit')
                                }
                              ]
                            : [])
                        ]
                      },
                      {
                        path: ROUTES.CONTAINERS.DETAIL.PATH,
                        lazy: () => import('@/pages/containers/Detail')
                      }
                    ]
                  : []),
                ...(authStore?.groups?.includes(UserRoles.ADMIN)
                  ? [
                      {
                        path: ROUTES.COMPANY_DAILY_VOLUME.PATH,
                        lazy: () => import('@/pages/company-daily-volume/Index')
                      },
                      ...(authStore?.groups?.includes(UserRoles.SUPPORT) || authStore?.groups?.includes(UserRoles.SUPERADMIN)
                        ? [
                            {
                              path: ROUTES.CALL_FORWARDING.PATH,
                              lazy: () => import('@/pages/call-forwarding/Index'),
                              children: [
                                {
                                  path: ROUTES.CALL_FORWARDING.EDIT.PATH,
                                  lazy: () => import('@/pages/call-forwarding/Edit')
                                },
                                {
                                  path: ROUTES.CALL_FORWARDING.DETAIL.PATH,
                                  lazy: () => import('@/pages/call-forwarding/Detail')
                                }
                              ]
                            }
                          ]
                        : [{}]),
                      {
                        path: ROUTES.COMPANY_UNLOADED_VOLUME.PATH,
                        lazy: () => import('@/pages/company-unloaded-volume/Index')
                      },
                      ...(authStore?.groups?.includes(UserRoles.SUPERADMIN)
                        ? [
                            {
                              path: ROUTES.ROUTE_CACHE.PATH,
                              lazy: () => import('@/pages/route-cache/Index'),
                              children: [
                                {
                                  path: ROUTES.ROUTE_CACHE.EDIT.PATH,
                                  lazy: () => import('@/pages/route-cache/Edit')
                                }
                              ]
                            }
                          ]
                        : [{}]),
                      {
                        path: ROUTES.XDOCKS_DAILY_VOLUME.PATH,
                        lazy: () => import('@/pages/xdocks-daily-volume/Index'),
                        children: [
                          {
                            path: ROUTES.XDOCKS_DAILY_VOLUME.DETAIL.PATH,
                            lazy: () => import('@/pages/xdocks-daily-volume/Detail')
                          }
                        ]
                      },
                      {
                        path: ROUTES.BULKCHANGE_USERS.PATH,
                        lazy: () => import('@/pages/bulkchange-users/Index')
                      },
                      {
                        path: ROUTES.COMPANY_EXIT.PATH,
                        lazy: () => import('@/pages/company-exit/Index')
                      },
                      {
                        path: ROUTES.XDOCK_DAILY_OPERATIONS.PATH,
                        lazy: () => import('@/pages/xdock-daily-operations/Index')
                      },
                      {
                        path: ROUTES.XDOCK_DAILY_OPERATIONS_HISTORY.PATH,
                        lazy: () => import('@/pages/xdock-daily-operations-history/Index')
                      },
                      {
                        path: ROUTES.TRANSFER_AUDIT.PATH,
                        lazy: () => import('@/pages/transfer-audit/Index')
                      },
                      {
                        path: ROUTES.ROUTE_DAILY_VOLUME.PATH,
                        lazy: () => import('@/pages/route-daily-volume/Index')
                      },
                      {
                        path: ROUTES.PERSONEL_ENTRANCE_EXIT.DETAIL.PATH,
                        lazy: () => import('@/pages/personel-entry-exit/Detail')
                      },
                      {
                        path: ROUTES.INFO.PATH,
                        lazy: () => import('@/pages/info/Index')
                      },
                      {
                        path: ROUTES.AGREEMENTS.PATH,
                        lazy: () => import('@/pages/agreements/Index')
                      },
                      {
                        path: ROUTES.AGREEMENTS.EDIT.PATH,
                        lazy: () => import('@/pages/agreements/Edit')
                      },
                      {
                        path: ROUTES.INVENTORY.PATH,
                        lazy: () => import('@/pages/inventory/Index')
                      },
                      {
                        path: ROUTES.INVENTORY.EDIT.PATH,
                        lazy: () => import('@/pages/inventory/Edit')
                      },
                      {
                        path: ROUTES.INVENTORY.DETAIL.PATH,
                        lazy: () => import('@/pages/inventory/Detail')
                      },
                      {
                        path: ROUTES.DEVICEREQUEST.PATH,
                        lazy: () => import('@/pages/device-request/Index')
                      },
                      {
                        path: ROUTES.DEVICEREQUEST.EDIT.PATH,
                        lazy: () => import('@/pages/device-request/Edit')
                      },
                      {
                        path: ROUTES.DEVICE_STATUS.PATH,
                        lazy: () => import('@/pages/device-status/Index')
                      },
                      {
                        path: ROUTES.DEVICE_STATUS.EDIT.PATH,
                        lazy: () => import('@/pages/device-status/Edit')
                      },
                      {
                        path: ROUTES.DEVICE_TYPE.PATH,
                        lazy: () => import('@/pages/device-type/Index')
                      },
                      {
                        path: ROUTES.DEVICE_TYPE.EDIT.PATH,
                        lazy: () => import('@/pages/device-type/Edit')
                      },
                      {
                        path: ROUTES.BRANDS.PATH,
                        lazy: () => import('@/pages/brands/Index')
                      },
                      {
                        path: ROUTES.BRANDS.EDIT.PATH,
                        lazy: () => import('@/pages/brands/Edit')
                      },
                      {
                        path: ROUTES.MODELS.PATH,
                        lazy: () => import('@/pages/models/Index')
                      },
                      {
                        path: ROUTES.MODELS.EDIT.PATH,
                        lazy: () => import('@/pages/models/Edit')
                      },
                      {
                        path: ROUTES.INVENTORY_LOGS.PATH,
                        lazy: () => import('@/pages/inventory-logs/Index')
                      },
                      {
                        path: ROUTES.INVENTORY_LOGS.DETAIL.PATH,
                        lazy: () => import('@/pages/inventory-logs/Detail')
                      },
                      {
                        index: true,
                        path: ROUTES.SEND_EMAIL.PATH,
                        lazy: () => import('@/pages/send-email/Index')
                      },
                      {
                        index: true,
                        path: ROUTES.SEND_EMAIL.EDIT.PATH,
                        lazy: () => import('@/pages/send-email/Edit')
                      },
                      ...(authStore?.groups?.includes(UserRoles.SUPERADMIN)
                        ? [
                            {
                              path: ROUTES.BULK_EMAIL.PATH,
                              lazy: () => import('@/pages/bulk-email/Index')
                            },
                            {
                              path: ROUTES.BULK_EMAIL.NEW.PATH,
                              lazy: () => import('@/pages/bulk-email/New')
                            },
                            {
                              path: ROUTES.BULK_EMAIL.DETAIL.PATH,
                              lazy: () => import('@/pages/bulk-email/Detail')
                            }
                          ]
                        : []),
                      // Plaka Okuma raporu — SUPERADMIN + OPERATION (BE rol kapısı da aynı)
                      ...(authStore?.groups?.includes(UserRoles.SUPERADMIN) || authStore?.groups?.includes(UserRoles.OPERATION)
                        ? [
                            {
                              path: ROUTES.REPORTS.PLATE_EVENTS.PATH,
                              lazy: () => import('@/pages/reports/PlateEvents')
                            }
                          ]
                        : []),
                      {
                        path: ROUTES.REPORTS.VEHICLE_PLANING.PATH,
                        lazy: () => import('@/pages/reports/VehiclePlaning')
                      },
                      {
                        path: ROUTES.REPORTS.PACKAGE_DESI_CHANGE_REPORT.PATH,
                        lazy: () => import('@/pages/reports/PackageDesiChangeReport')
                      },
                      {
                        path: ROUTES.REPORTS.PACKAGE_CONTROL_REPORT.PATH,
                        lazy: () => import('@/pages/reports/PackageControlReport')
                      },
                      {
                        path: ROUTES.REPORTS.SUPER_ADMIN_HISTORY_REPORT.PATH,
                        lazy: () => import('@/pages/reports/SuperAdminHistoryReport')
                      },
                      {
                        path: ROUTES.REPORTS.CALL_FORWARDING.PATH,
                        lazy: () => import('@/pages/reports/CallForwarding')
                      },
                      {
                        path: ROUTES.REPORTS.ROUTE_SESSION.PATH,
                        lazy: () => import('@/pages/reports/RouteSession')
                      },
                      {
                        path: ROUTES.REPORTS.ROUTE_SESSION_SPECIAL.PATH,
                        lazy: () => import('@/pages/reports/RouteSessionSpecial')
                      },
                      {
                        path: ROUTES.REPORTS.ROUTE_SESSION_FILE_CHECK.PATH,
                        lazy: () => import('@/pages/reports/RouteSessionFileCheck')
                      },
                      {
                        path: ROUTES.REPORTS.INDEX_ROUTE_SESSIONS_OLUSTURULDU.PATH,
                        lazy: () => import('@/pages/reports/IndexRouteSessionsOlusturuldu')
                      },
                      {
                        path: ROUTES.REPORTS.VEHICLES.PATH,
                        lazy: () => import('@/pages/vehicles-report/Index')
                      },
                      {
                        path: ROUTES.REPORTS.TRANSFER_VEHICLE_ENTRY.PATH,
                        lazy: () => import('@/pages/reports/TransferVehicleEntry')
                      },
                      {
                        path: ROUTES.REPORTS.TRANSFER_ROUTE_REPORT.PATH,
                        lazy: () => import('@/pages/reports/TransferRouteReport')
                      },
                      {
                        path: ROUTES.REPORTS.COMPANIES_DETAIL_REPORT.PATH,
                        lazy: () => import('@/pages/reports/CompaniesDetailReport')
                      },
                      {
                        path: ROUTES.REPORTS.VENDORS_DETAIL_REPORT.PATH,
                        lazy: () => import('@/pages/reports/VendorsDetailReport')
                      },
                      {
                        path: ROUTES.REPORTS.VEHICLE_REQUEST_REPORT.PATH,
                        lazy: () => import('@/pages/reports/VehicleRequestReport')
                      },
                      {
                        path: ROUTES.REPORTS.CARGO_IN_WAREHOUSE_REPORT.PATH,
                        lazy: () => import('@/pages/reports/CargoInWarehouse')
                      },
                      {
                        path: ROUTES.REPORTS.DISTRIBUTION_EXPEDITIONS_REPORT.PATH,
                        lazy: () => import('@/pages/reports/DistributionExpeditionsReport')
                      },
                      {
                        path: ROUTES.CITY_DISTRICT_ROUTE_EDIT.PATH,
                        lazy: () => import('@/pages/cities-districts-route-edit/Index'),
                        children: [
                          {
                            path: ROUTES.CITY_DISTRICT_ROUTE_EDIT.EDIT.PATH,
                            lazy: () => import('@/pages/cities-districts-route-edit/Edit')
                          }
                        ]
                      }
                    ]
                  : []),
                ...(authStore?.groups?.includes(UserRoles.ADMIN) ||
                authStore?.groups?.includes(UserRoles.SECURITY) ||
                authStore?.groups?.includes(UserRoles.XDOCK_ADMIN)
                  ? [
                      {
                        path: ROUTES.WAREHOUSE_VEHICLE_ENTRANCE.PATH,
                        lazy: () => import('@/pages/warehouse-vehicle-entrance/Index')
                      },
                      {
                        path: ROUTES.DEPOT_LAYOUT.PATH,
                        lazy: () => import('@/pages/depot-layout/Index')
                      },
                      {
                        path: ROUTES.RAMP_BOARD.PATH,
                        lazy: () => import('@/pages/ramp-board/Index')
                      },
                      {
                        path: ROUTES.RAMP_ANALYTICS.PATH,
                        lazy: () => import('@/pages/ramp-analytics/Index')
                      }
                    ]
                  : []),
                // Bölge Araç Yoklaması (plan: docs/BOLGE_ARAC_YOKLAMA_PLANI.md v1.3) — grup listeleri SidebarMenu ile
                // AYNI sabitlerden (utils/vehicleAttendance) okunur; menü–rota uyumsuzluğu oluşamaz
                ...(hasAnyGroup(authStore?.groups, ATTENDANCE_GROUPS)
                  ? [
                      {
                        path: ROUTES.XDOCK_VEHICLE_ATTENDANCE.PATH,
                        lazy: () => import('@/pages/xdock-vehicle-attendance/Index')
                      },
                      {
                        path: ROUTES.XDOCK_REGION_ALERTS.PATH,
                        lazy: () => import('@/pages/xdock-region-alerts/Index')
                      }
                    ]
                  : []),
                ...(hasAnyGroup(authStore?.groups, ASSIGNMENT_PAGE_GROUPS)
                  ? [
                      {
                        path: ROUTES.VEHICLE_REGION_ASSIGNMENTS.PATH,
                        lazy: () => import('@/pages/vehicle-region-assignments/Index')
                      }
                    ]
                  : []),
                ...(hasAnyGroup(authStore?.groups, AREA_PAGE_GROUPS)
                  ? [
                      {
                        path: ROUTES.VEHICLE_REGION_AREAS.PATH,
                        lazy: () => import('@/pages/vehicle-region-areas/Index')
                      }
                    ]
                  : []),
                // Talep Tahmini - SUPERADMIN + OPERATION
                ...(authStore?.groups?.includes(UserRoles.SUPERADMIN) || authStore?.groups?.includes(UserRoles.OPERATION)
                  ? [
                      {
                        path: ROUTES.DEMAND_FORECAST.PATH,
                        lazy: () => import('@/pages/demand-forecast/Index')
                      },
                      {
                        path: ROUTES.DEMAND_FORECAST_ACCURACY.PATH,
                        lazy: () => import('@/pages/demand-forecast/Accuracy')
                      },
                      {
                        path: ROUTES.DEMAND_FORECAST_METHODOLOGY.PATH,
                        lazy: () => import('@/pages/demand-forecast/Methodology')
                      }
                    ]
                  : []),
                // Güvenlik İzleme (Plaka Okuma + Kamera Uyarıları) - SUPERADMIN + SECURITY + OPERATION
                ...(authStore?.groups?.includes(UserRoles.SUPERADMIN) ||
                authStore?.groups?.includes(UserRoles.SECURITY) ||
                authStore?.groups?.includes(UserRoles.OPERATION)
                  ? [
                      {
                        path: ROUTES.PLAKA_OKUMA.PATH,
                        lazy: () => import('@/pages/security/PlakaOkuma')
                      }
                    ]
                  : []),
                // Satın Alma — sayfa içi görünürlük izin anahtarlarından (grant) çözülür
                {
                  path: ROUTES.PURCHASE_ORDERS.PATH,
                  lazy: () => import('@/pages/purchase-orders/Index'),
                  children: [
                    {
                      path: ROUTES.PURCHASE_ORDERS.EDIT.PATH,
                      lazy: () => import('@/pages/purchase-orders/Edit')
                    }
                  ]
                },
                {
                  path: ROUTES.PURCHASE_REQUESTS.PATH,
                  lazy: () => import('@/pages/purchase-requests/Index'),
                  children: [
                    {
                      path: ROUTES.PURCHASE_REQUESTS.EDIT.PATH,
                      lazy: () => import('@/pages/purchase-requests/Edit')
                    }
                  ]
                },
                {
                  path: ROUTES.PURCHASING_APPROVALS.PATH,
                  lazy: () => import('@/pages/purchasing-approvals/Index')
                },
                {
                  path: ROUTES.GOODS_RECEIPTS.PATH,
                  lazy: () => import('@/pages/goods-receipts/Index')
                },
                {
                  path: ROUTES.PURCHASING_ANALYTICS.PATH,
                  lazy: () => import('@/pages/purchasing-analytics/Index')
                },
                {
                  path: ROUTES.PURCHASING_REPORTS.PATH,
                  lazy: () => import('@/pages/purchasing-reports/Index')
                },
                {
                  path: ROUTES.INVOICE_MATCHING.PATH,
                  lazy: () => import('@/pages/invoice-matching/Index')
                },
                {
                  path: ROUTES.EFATURA_ARCHIVE.PATH,
                  lazy: () => import('@/pages/efatura-archive/Index')
                },
                {
                  path: ROUTES.EFATURA_ARCHIVE.DETAIL.PATH,
                  lazy: () => import('@/pages/efatura-archive/Detail')
                },
                {
                  path: ROUTES.DIA_SUPPLIERS.PATH,
                  lazy: () => import('@/pages/dia-suppliers/Index')
                },
                ...(authStore?.groups?.includes(UserRoles.ADMIN) || authStore?.groups?.includes(UserRoles.SUPERADMIN)
                  ? [
                      {
                        path: ROUTES.DIA_API_LOGS.PATH,
                        lazy: () => import('@/pages/dia-api-logs/Index')
                      }
                    ]
                  : []),
                // DİA Durumu (işçi/kuyruk/ayarlar) — DİA yönetici grupları
                ...(authStore?.groups?.includes(UserRoles.ADMIN) ||
                authStore?.groups?.includes(UserRoles.SUPERADMIN) ||
                authStore?.groups?.includes(UserRoles.ACCOUNTING) ||
                authStore?.groups?.includes(UserRoles.PURCHASING_ADMIN)
                  ? [
                      {
                        path: ROUTES.DIA_DURUM.PATH,
                        lazy: () => import('@/pages/dia-durum/Index')
                      }
                    ]
                  : []),
                {
                  path: ROUTES.PURCHASING_RFQS.PATH,
                  lazy: () => import('@/pages/rfq/Index'),
                  children: [
                    {
                      path: ROUTES.PURCHASING_RFQS.EDIT.PATH,
                      lazy: () => import('@/pages/rfq/Edit')
                    }
                  ]
                },
                ...(authStore?.groups?.includes(UserRoles.ADMIN) ||
                authStore?.groups?.includes(UserRoles.SUPERADMIN) ||
                authStore?.groups?.includes(UserRoles.PURCHASING_ADMIN)
                  ? [
                      {
                        path: ROUTES.PURCHASING_WORKFLOWS.PATH,
                        lazy: () => import('@/pages/purchasing-workflows/Index')
                      },
                      {
                        path: ROUTES.PURCHASING_DEFINITIONS.PATH,
                        lazy: () => import('@/pages/purchasing-definitions/Index')
                      },
                      {
                        path: ROUTES.PURCHASING_ROLES.PATH,
                        lazy: () => import('@/pages/purchasing-roles/Index')
                      },
                      {
                        path: ROUTES.PURCHASING_ORG.PATH,
                        lazy: () => import('@/pages/purchasing-org/Index')
                      },
                      {
                        path: ROUTES.PURCHASING_CUSTOM_FIELDS.PATH,
                        lazy: () => import('@/pages/purchasing-custom-fields/Index')
                      },
                      {
                        path: ROUTES.PURCHASING_GUIDE.PATH,
                        lazy: () => import('@/pages/purchasing-guide/Index')
                      }
                    ]
                  : []),
                ...(authStore?.groups?.includes(UserRoles.ADMIN)
                  ? [
                      {
                        path: ROUTES.SUPPLIERS.PATH,
                        lazy: () => import('@/pages/suppliers/Index'),
                        children: [
                          {
                            path: ROUTES.SUPPLIERS.EDIT.PATH,
                            lazy: () => import('@/pages/suppliers/Edit')
                          }
                        ]
                      },
                      {
                        path: ROUTES.PACK_TYPES.PATH,
                        lazy: () => import('@/pages/pack-types/Index'),
                        children: [
                          {
                            path: ROUTES.PACK_TYPES.EDIT.PATH,
                            lazy: () => import('@/pages/pack-types/Edit')
                          }
                        ]
                      },
                      {
                        path: ROUTES.PACKAGE_CONTROL.PATH,
                        lazy: () => import('@/pages/package-control/Index')
                      },
                      {
                        path: ROUTES.PACKAGE_CONTROL.DETAIL.PATH,
                        lazy: () => import('@/pages/package-control/Detail')
                      },
                      {
                        path: ROUTES.VEHICLES.PATH,
                        lazy: () => import('@/pages/vehicles/Index'),
                        children: [
                          {
                            path: ROUTES.VEHICLES.EDIT.PATH,
                            lazy: () => import('@/pages/vehicles/Edit')
                          }
                        ]
                      },
                      {
                        path: ROUTES.VEHICLE_TRACKING.PATH,
                        lazy: () => import('@/pages/vehicle-tracking/Index')
                      },
                      {
                        path: ROUTES.ROUTE.PATH,
                        lazy: () => import('@/pages/route/Index'),
                        children: [
                          {
                            path: ROUTES.ROUTE.EDIT.PATH,
                            lazy: () => import('@/pages/route/Edit')
                          }
                        ]
                      },
                      {
                        path: ROUTES.CREATE_MICRO_ROUTE.PATH,
                        lazy: () => import('@/pages/create-micro-route/Index'),
                        children: [
                          {
                            path: ROUTES.CREATE_MICRO_ROUTE.EDIT.PATH,
                            lazy: () => import('@/pages/create-micro-route/Edit')
                          }
                        ]
                      },
                      {
                        path: ROUTES.REGION.PATH,
                        lazy: () => import('@/pages/region-detail/Index'),
                        children: [
                          {
                            path: ROUTES.REGION.EDIT.PATH,
                            lazy: () => import('@/pages/region-detail/Edit')
                          },
                          {
                            path: ROUTES.REGION.CONTACT_EDIT.PATH,
                            lazy: () => import('@/pages/region-detail/ContactDetailEdit')
                          }
                        ]
                      },
                      {
                        path: ROUTES.REGION.DETAIL.PATH,
                        lazy: () => import('@/pages/region-detail/Detail')
                      },
                      {
                        path: ROUTES.CREATE_COMPANY_ROUTE.PATH,
                        lazy: () => import('@/pages/create-company-route/Index'),
                        children: [
                          {
                            path: ROUTES.CREATE_COMPANY_ROUTE.EDIT.PATH,
                            lazy: () => import('@/pages/create-company-route/Edit')
                          }
                        ]
                      },
                      {
                        path: ROUTES.MICRO_ROUTE_TRANSPORTATION.PATH,
                        lazy: () => import('@/pages/micro-route-transportation/Index'),
                        children: [
                          {
                            path: ROUTES.MICRO_ROUTE_TRANSPORTATION.EDIT.PATH,
                            lazy: () => import('@/pages/micro-route-transportation/Edit')
                          }
                        ]
                      },
                      {
                        path: ROUTES.REGION_MICRO_TRANSPORTATION.PATH,
                        lazy: () => import('@/pages/region-micro-transportation/Index'),
                        children: [
                          {
                            path: ROUTES.REGION_MICRO_TRANSPORTATION.EDIT.PATH,
                            lazy: () => import('@/pages/region-micro-transportation/Edit')
                          }
                        ]
                      },
                      {
                        path: ROUTES.PROVINCE_ROUTE_TRANSPORTATION.PATH,
                        lazy: () => import('@/pages/province-route-transportation/Index'),
                        children: [
                          {
                            path: ROUTES.PROVINCE_ROUTE_TRANSPORTATION.EDIT.PATH,
                            lazy: () => import('@/pages/province-route-transportation/Edit')
                          }
                        ]
                      },
                      {
                        path: ROUTES.DISTRICT_ROUTE_TRANSPORTATION.PATH,
                        lazy: () => import('@/pages/district-route-transportation/Index'),
                        children: [
                          {
                            path: ROUTES.DISTRICT_ROUTE_TRANSPORTATION.EDIT.PATH,
                            lazy: () => import('@/pages/district-route-transportation/Edit')
                          }
                        ]
                      }
                    ]
                  : []),
                // Fiyat Hesaplama Logları — ADMIN (SUPERADMIN bloğunda da tanımlı; SUPERADMIN çoğu zaman ADMIN grubunu taşımaz)
                ...(authStore?.groups?.includes(UserRoles.ADMIN) && !authStore?.groups?.includes(UserRoles.SUPERADMIN)
                  ? [
                      {
                        path: ROUTES.PRICE_ESTIMATE_LOGS.PATH,
                        lazy: () => import('@/pages/price-estimate-logs/Index')
                      }
                    ]
                  : []),
                ...(authStore?.groups?.includes(UserRoles.ADMIN) || authStore?.groups?.includes(UserRoles.SECURITY)
                  ? [
                      {
                        path: ROUTES.PERSONEL.PATH,
                        lazy: () => import('@/pages/personel/Index'),
                        children: [
                          {
                            path: ROUTES.PERSONEL.EDIT.PATH,
                            lazy: () => import('@/pages/personel/Edit')
                          }
                        ]
                      },
                      {
                        path: ROUTES.PERSONEL.DETAIL.PATH,
                        lazy: () => import('@/pages/personel/Detail')
                      },
                      {
                        path: ROUTES.PERSONEL_ENTRANCE_EXIT.PATH,
                        lazy: () => import('@/pages/personel-entry-exit/Index'),
                        children: [
                          {
                            path: ROUTES.PERSONEL_ENTRANCE_EXIT.EDIT.PATH,
                            lazy: () => import('@/pages/personel-entry-exit/Edit')
                          }
                        ]
                      },
                      {
                        path: ROUTES.PERSONEL_SUPPLIER.PATH,
                        lazy: () => import('@/pages/personel-supplier/Index'),
                        children: [
                          {
                            path: ROUTES.PERSONEL_SUPPLIER.EDIT.PATH,
                            lazy: () => import('@/pages/personel-supplier/Edit')
                          }
                        ]
                      },
                      {
                        path: ROUTES.SHIFTS.PATH,
                        lazy: () => import('@/pages/shifts/Index'),
                        children: [
                          {
                            path: ROUTES.SHIFTS.EDIT.PATH,
                            lazy: () => import('@/pages/shifts/Edit')
                          }
                        ]
                      },
                      {
                        path: ROUTES.DELIVERY_TIMES.PATH,
                        lazy: () => import('@/pages/delivery-times/Index'),
                        children: [
                          {
                            path: ROUTES.DELIVERY_TIMES.EDIT.PATH,
                            lazy: () => import('@/pages/delivery-times/Edit')
                          }
                        ]
                      },
                      {
                        path: ROUTES.PUBLIC_HOLIDAYS.PATH,
                        lazy: () => import('@/pages/public-holidays/Index')
                      },
                      {
                        path: ROUTES.TRANSFER_ROUTE_TIMES.PATH,
                        lazy: () => import('@/pages/transfer-route-times/Index')
                      },
                      {
                        path: ROUTES.POINT_DISTRICTS.PATH,
                        lazy: () => import('@/pages/point-districts/Index')
                      },
                      {
                        path: ROUTES.DOOR.PATH,
                        lazy: () => import('@/pages/door/Index'),
                        children: [
                          {
                            path: ROUTES.DOOR.EDIT.PATH,
                            lazy: () => import('@/pages/door/Edit')
                          }
                        ]
                      },
                      {
                        path: ROUTES.MISSION.PATH,
                        lazy: () => import('@/pages/mission/Index'),
                        children: [
                          {
                            path: ROUTES.MISSION.EDIT.PATH,
                            lazy: () => import('@/pages/mission/Edit')
                          }
                        ]
                      }
                    ]
                  : []),
                // ...(authStore?.groups?.includes(UserRoles.ADMIN) ||
                // authStore?.groups?.includes(UserRoles.XDOCK_ADMIN) ||
                // authStore?.groups?.includes(UserRoles.XDOCK)
                //   ? [
                //       {
                //         index: true,
                //         path: ROUTES.SHIPMENT_DELIVERY.PATH,
                //         lazy: () => import('@/pages/shipment-delivery/Index')
                //       }
                //     ]
                //   : []),
                ...(authStore?.groups?.includes(UserRoles.ADMIN) ||
                authStore?.groups?.includes(UserRoles.XDOCK_ADMIN) ||
                authStore?.groups?.includes(UserRoles.COMPANY_DEPO)
                  ? [
                      {
                        path: ROUTES.VEHICLE_PLANING.PATH,
                        lazy: () => import('@/pages/vehicle-planing/Index'),
                        children: [
                          {
                            path: ROUTES.VEHICLE_PLANING.EDIT.PATH,
                            lazy: () => import('@/pages/vehicle-planing/Edit')
                          }
                        ]
                      }
                    ]
                  : []),
                // SUPPLIER_USER, XDOCK, SECURITY, OPERATION - Araçlar ve Araç Planlama sayfalarına erişim
                ...(authStore?.groups?.includes(UserRoles.SUPPLIER_USER) ||
                authStore?.groups?.includes(UserRoles.XDOCK) ||
                authStore?.groups?.includes(UserRoles.SECURITY) ||
                authStore?.groups?.includes(UserRoles.OPERATION)
                  ? [
                      {
                        path: ROUTES.VEHICLES.PATH,
                        lazy: () => import('@/pages/vehicles/Index'),
                        children: [
                          {
                            path: ROUTES.VEHICLES.EDIT.PATH,
                            lazy: () => import('@/pages/vehicles/Edit')
                          }
                        ]
                      },
                      {
                        path: ROUTES.VEHICLE_PLANING.PATH,
                        lazy: () => import('@/pages/vehicle-planing/Index'),
                        children: [
                          {
                            path: ROUTES.VEHICLE_PLANING.EDIT.PATH,
                            lazy: () => import('@/pages/vehicle-planing/Edit')
                          }
                        ]
                      },
                      {
                        path: ROUTES.REPORTS.VEHICLE_PLANING.PATH,
                        lazy: () => import('@/pages/reports/VehiclePlaning')
                      }
                    ]
                  : []),
                // Harici Kargo (plan: docs/HARICI_KARGO_PAKET_PLANI.md v1.1)
                ...([
                  UserRoles.ADMIN,
                  UserRoles.OPERATION,
                  UserRoles.SUPERADMIN,
                  UserRoles.XDOCK_ADMIN,
                  UserRoles.XDOCK,
                  UserRoles.ACCOUNTING,
                  UserRoles.SUPPORT
                ].some((r) => authStore?.groups?.includes(r))
                  ? [
                      {
                        path: ROUTES.HARICI_KARGO.PATH,
                        lazy: () => import('@/pages/harici-kargo/Index')
                      },
                      {
                        path: `${ROUTES.HARICI_KARGO.PATH}/${ROUTES.HARICI_KARGO.CARRIERS.PATH}`,
                        lazy: () => import('@/pages/harici-kargo/Carriers')
                      }
                    ]
                  : []),
                // Dedike Nakliye (plan: docs/DEDIKE_NAKLIYE_SATIS_PLANI.md) — City rolleri
                ...([UserRoles.OPERATION, UserRoles.ADMIN, UserRoles.SUPERADMIN, UserRoles.ACCOUNTING, UserRoles.SALES].some((r) =>
                  authStore?.groups?.includes(r)
                )
                  ? [
                      {
                        path: ROUTES.DEDIKE_NAKLIYE.PATH,
                        lazy: () => import('@/pages/dedike-nakliye/Index')
                      },
                      {
                        path: `${ROUTES.DEDIKE_NAKLIYE.PATH}/${ROUTES.DEDIKE_NAKLIYE.EDIT.PATH}`,
                        lazy: () => import('@/pages/dedike-nakliye/Edit')
                      },
                      {
                        path: `${ROUTES.DEDIKE_NAKLIYE.PATH}/${ROUTES.DEDIKE_NAKLIYE.INVOICES.PATH}`,
                        lazy: () => import('@/pages/dedike-nakliye/Invoices')
                      },
                      {
                        path: `${ROUTES.DEDIKE_NAKLIYE.PATH}/${ROUTES.DEDIKE_NAKLIYE.INVOICE_DETAIL.PATH}`,
                        lazy: () => import('@/pages/dedike-nakliye/InvoiceDetail')
                      },
                      {
                        path: `${ROUTES.DEDIKE_NAKLIYE.PATH}/${ROUTES.DEDIKE_NAKLIYE.RECONCILIATION.PATH}`,
                        lazy: () => import('@/pages/dedike-nakliye/Reconciliation')
                      },
                      {
                        path: `${ROUTES.DEDIKE_NAKLIYE.PATH}/${ROUTES.DEDIKE_NAKLIYE.REPORTS.PATH}`,
                        lazy: () => import('@/pages/dedike-nakliye/Reports')
                      },
                      {
                        path: `${ROUTES.DEDIKE_NAKLIYE.PATH}/${ROUTES.DEDIKE_NAKLIYE.PENDING.PATH}`,
                        lazy: () => import('@/pages/dedike-nakliye/Pending')
                      }
                    ]
                  : []),
                // Dedike Nakliye — FİRMA görünümü (K22): yalnız firma rolleri
                ...([UserRoles.COMPANY, UserRoles.COMPANY_ADMIN, UserRoles.COMPANY_DEPO].some((r) => authStore?.groups?.includes(r))
                  ? [
                      {
                        path: `${ROUTES.DEDIKE_NAKLIYE.PATH}/${ROUTES.DEDIKE_NAKLIYE.COMPANY.PATH}`,
                        lazy: () => import('@/pages/dedike-nakliye/firma/Index')
                      },
                      {
                        path: `${ROUTES.DEDIKE_NAKLIYE.PATH}/${ROUTES.DEDIKE_NAKLIYE.COMPANY_INVOICES.PATH}`,
                        lazy: () => import('@/pages/dedike-nakliye/firma/Invoices')
                      },
                      {
                        path: `${ROUTES.DEDIKE_NAKLIYE.PATH}/${ROUTES.DEDIKE_NAKLIYE.COMPANY_INVOICE_DETAIL.PATH}`,
                        lazy: () => import('@/pages/dedike-nakliye/firma/InvoiceDetail')
                      }
                    ]
                  : []),
                {
                  path: ROUTES.CUSTOMER_TRACKING.PATH,
                  lazy: () => import('@/pages/customer-tracking/Index')
                },
                {
                  path: ROUTES.CUSTOMER_TRACKING.DETAIL.PATH,
                  lazy: () => import('@/pages/customer-tracking/Detail')
                },
                {
                  path: ROUTES.CUSTOMER_TRACKING.ALERTS.PATH,
                  lazy: () => import('@/pages/customer-tracking/Alerts')
                },
                {
                  path: ROUTES.DELIVERY_CALCULATION.PATH,
                  lazy: () => import('@/pages/delivery-calculation/Index')
                },
                {
                  path: ROUTES.DELIVERY_REPORT.PATH,
                  lazy: () => import('@/pages/delivery-report/Index')
                },
                {
                  path: ROUTES.DELIVERY_CALCULATION_REGION.PATH,
                  lazy: () => import('@/pages/delivery-calculation-region/Index')
                },
                // Satış CRM (plan v3.1): rota gate'i menüyle AYNI — op-admin
                // (ADMIN) BİLEREK dışarıda, erişim yalnız SALES satırından
                ...(authStore?.groups?.includes(UserRoles.SALES) || authStore?.groups?.includes(UserRoles.SUPERADMIN)
                  ? [
                      {
                        path: ROUTES.SALES_CRM.PATH,
                        lazy: () => import('@/pages/sales-crm/Index')
                      },
                      {
                        path: ROUTES.SALES_CRM.LEADS.PATH,
                        lazy: () => import('@/pages/sales-crm/Leads')
                      },
                      {
                        path: ROUTES.SALES_CRM.LEADS.DETAIL.PATH,
                        lazy: () => import('@/pages/sales-crm/LeadDetail')
                      },
                      {
                        path: ROUTES.SALES_CRM.AGENDA.PATH,
                        lazy: () => import('@/pages/sales-crm/Agenda')
                      },
                      {
                        path: ROUTES.SALES_CRM.QUOTES.PATH,
                        lazy: () => import('@/pages/sales-crm/Quotes')
                      },
                      {
                        path: ROUTES.SALES_CRM.REPORTS.PATH,
                        lazy: () => import('@/pages/sales-crm/Reports')
                      },
                      {
                        path: ROUTES.SALES_CRM.SETTINGS.PATH,
                        lazy: () => import('@/pages/sales-crm/Settings')
                      }
                    ]
                  : []),
                {
                  path: ROUTES.UNDELIVERED_PACKAGES.PATH,
                  lazy: () => import('@/pages/undelivered-packages/Index')
                },
                {
                  path: ROUTES.UNDELIVERED_PACKAGES_LOGS.PATH,
                  lazy: () => import('@/pages/undelivered-packages-logs/Index')
                },
                {
                  path: ROUTES.UNIT_DESI_AVERAGES.PATH,
                  lazy: () => import('@/pages/unit-desi-averages/Index')
                },
                {
                  path: ROUTES.COMPANY_VEHICLE_VISITS.PATH,
                  lazy: () => import('@/pages/company-vehicle-visits/Index')
                },
                {
                  path: ROUTES.LOW_VOLUME_COMPANIES.PATH,
                  lazy: () => import('@/pages/low-volume-companies/Index')
                },
                {
                  path: ROUTES.EXTERNAL_CARGO_TRACKING.PATH,
                  lazy: () => import('@/pages/external-cargo-tracking/Index')
                },
                {
                  path: ROUTES.FUEL.PATH,
                  lazy: () => import('@/pages/fuel/Index')
                },
                {
                  path: ROUTES.FUEL_STATIONS.PATH,
                  lazy: () => import('@/pages/fuel-stations/Index')
                },
                {
                  path: ROUTES.CARGO_IN_WAREHOUSE.PATH,
                  lazy: () => import('@/pages/cargo-in-warehouse/Index')
                },
                {
                  path: ROUTES.CARGO_IN_WAREHOUSE_LOGS.PATH,
                  lazy: () => import('@/pages/cargo-in-warehouse-logs/Index')
                },
                {
                  path: ROUTES.CARGO_IN_WAREHOUSE_LOGS.DETAIL.PATH,
                  lazy: () => import('@/pages/cargo-in-warehouse-logs/Detail')
                },
                ...(authStore?.groups?.includes(UserRoles.SUPERADMIN) || authStore?.groups?.includes(UserRoles.ADMIN)
                  ? [
                      {
                        path: ROUTES.LIVE_MONITOR.PATH,
                        lazy: () => import('@/pages/live-monitor/Index')
                      }
                    ]
                  : []),
                ...(authStore?.groups?.includes(UserRoles.SUPERADMIN) ||
                authStore?.groups?.includes(UserRoles.ADMIN) ||
                authStore?.groups?.includes(UserRoles.SUPPORT)
                  ? [
                      {
                        path: ROUTES.REMOTE_ACCESS.PATH,
                        lazy: () => import('@/pages/remote-access/Index')
                      },
                      {
                        path: ROUTES.REMOTE_ACCESS_SESSION.PATH,
                        lazy: () => import('@/pages/remote-access/RemoteSession')
                      }
                    ]
                  : []),
                ...(authStore?.groups?.includes(UserRoles.SUPERADMIN)
                  ? [
                      {
                        path: ROUTES.DIA_CARI_SYNC.PATH,
                        lazy: () => import('@/pages/dia-sync/DiaCariSync')
                      },
                      {
                        path: ROUTES.DIA_SYNC_HISTORY.PATH,
                        lazy: () => import('@/pages/dia-sync/DiaSyncHistory')
                      },
                      {
                        path: ROUTES.DIA_UNMATCHED.PATH,
                        lazy: () => import('@/pages/dia-sync/DiaUnmatched')
                      },
                      {
                        path: ROUTES.DIA_CARI_KARTLAR.PATH,
                        lazy: () => import('@/pages/dia-sync/DiaCariKartlar')
                      },
                      {
                        path: ROUTES.DIA_IRSALIYE_AKTAR.PATH,
                        lazy: () => import('@/pages/dia-sync/DiaIrsaliyeAktar')
                      }
                    ]
                  : []),
                // Faturalama (DİA Sevk İrsaliyesi) - yalnız SUPERADMIN + ACCOUNTING
                ...(authStore?.groups?.includes(UserRoles.SUPERADMIN) || authStore?.groups?.includes(UserRoles.ACCOUNTING)
                  ? [
                      {
                        path: ROUTES.DIA_IRSALIYE_AYARLAR.PATH,
                        lazy: () => import('@/pages/dia-irsaliye/Ayarlar')
                      },
                      {
                        path: ROUTES.DIA_IRSALIYE_OLUSTUR.PATH,
                        lazy: () => import('@/pages/dia-irsaliye/Olustur')
                      }
                    ]
                  : []),
                {
                  path: '*',
                  lazy: () => import('@/pages/error/404')
                }
              ]
            },
            {
              lazy: () => import('@/pages/daily-route/PrintA4'),
              path: ROUTES.DAILY_ROUTE.PRINT_A4.PATH
            },
            {
              lazy: () => import('@/pages/distribution-expeditions/PrintA4'),
              path: ROUTES.DISTRIBUTION_EXPEDITIONS.PRINT_A4.PATH
            },
            {
              // Giriş yapmış kullanıcı da mail linkini açabilir — panel şablonu dışında
              lazy: () => import('@/pages/lost-packages/PublicRespond'),
              path: ROUTES.LOST_PACKAGES.RESPOND.PATH
            }
          ])}
        />
      );
    } else {
      return (
        <RouterProvider
          router={createBrowserRouter([
            {
              lazy: () => import('@/layouts/NoAuthorityLayout'),
              children: [
                {
                  index: true,
                  path: '*',
                  lazy: () => import('@/pages/no-authority/Index')
                }
              ]
            }
          ])}
          fallbackElement={<Loader />}
        />
      );
    }
  }
}
